import { SnackbarContext } from "@/components/snackbar/Snackbar.context";
import { AppleLogin } from "./apple-login/AppleLogin";
import { FacebookLogin } from "./facebook-login/FacebookLogin";
import { ImageWithFallback } from "@/components/image/ImageWithFallback";
import { remoteLogger } from "@/utils/remoteLogger";
import useTranslation from "next-translate/useTranslation";
import type { ReactElement } from "react";
import React, { useContext } from "react";
import styles from "./SocialLogin.module.scss";
import { useAuthentication } from "@/context/Authentication";
import {
  DEFAULT_NOTIFICATION_TIMEOUT_MS,
  SeverityType,
} from "@/components/snackbar/Snackbar";
import { PROFILE_FOLLOWING_ROUTE } from "@/constants/routes.constant";
import { GoogleLogin } from "./google-login/GoogleLogin";
import { Button } from "@/components/button/Button";
import { useLoginRedirectProps } from "@/utils/hooks/useLoginRedirectProps";
import type { TrackingEvent } from "@/modules/analytics";
import { RudderstackEventName, trackingService } from "@/modules/analytics";

interface SocialLoginProps {
  signIn?: boolean;
  onEmailClick: () => void;
}

const SocialLogin: React.FC<SocialLoginProps> = ({
  signIn = false,
  onEmailClick,
}): ReactElement => {
  const { t } = useTranslation("web-payments");
  const showNotification = useContext(SnackbarContext);
  const { signOut, startSignInViaGoogle, startSignInViaFacebook } =
    useAuthentication();
  const { origin, redirectUrl } = useLoginRedirectProps(
    PROFILE_FOLLOWING_ROUTE,
  );

  const handleTrackingOnClick = () => {
    const trackingEvent: TrackingEvent = {
      params: {
        login_origin: origin,
        login_source: redirectUrl,
      },
      trackers: [
        {
          kind: "rudderstack",
          extraParams: { name: RudderstackEventName.LoginClicked },
        },
      ],
    };

    trackingService.sendEvent(trackingEvent);
  };

  const handleFailure = (provider: string, err?: unknown, data?: unknown) => {
    handleTrackingOnClick();
    remoteLogger.error(err, data);

    showNotification?.({
      message: t("SSO_FAILED", { provider }),
      type: SeverityType.error,
      dismissible: true,
      autoHideDuration: DEFAULT_NOTIFICATION_TIMEOUT_MS,
    });

    signOut();
  };

  return (
    <div className={styles.socialBtnWrapper}>
      <FacebookLogin
        className={styles.socialButton}
        onClick={handleTrackingOnClick}
        onSuccess={(response) => {
          startSignInViaFacebook({
            screen: signIn ? "sign-in" : "sign-up",
            token: response.accessToken,
            redirectUrl: redirectUrl,
            origin: origin,
          });
        }}
        onFailure={handleFailure}
      >
        <ImageWithFallback
          alt={t("ICON_FACEBOOK_LOGO_ALT_TEXT")}
          src="https://images.onefootball.com/cw/icons/facebook-v3.svg"
          darkModeSrc="https://images.onefootball.com/cw/icons/facebook-v3.svg"
          width={32}
          height={32}
        />
      </FacebookLogin>

      <GoogleLogin
        className={styles.socialButton}
        onClick={handleTrackingOnClick}
        onSuccess={(response) => {
          handleTrackingOnClick();

          startSignInViaGoogle({
            screen: signIn ? "sign-in" : "sign-up",
            token: response,
            redirectUrl: redirectUrl,
            origin: origin,
          });
        }}
        onError={handleFailure}
      >
        <ImageWithFallback
          alt={t("ICON_GOOGLE_LOGO_ALT_TEXT")}
          src="https://images.onefootball.com/cw/icons/google-v3-light.svg"
          darkModeSrc="https://images.onefootball.com/cw/icons/google-v3-dark.svg"
          width={32}
          height={32}
        />
      </GoogleLogin>

      <AppleLogin
        onClick={handleTrackingOnClick}
        className={styles.socialButton}
        state={redirectUrl}
      >
        <ImageWithFallback
          alt={t("ICON_APPLE_LOGO_ALT_TEXT")}
          src="https://images.onefootball.com/cw/icons/apple-v2-light.svg"
          darkModeSrc="https://images.onefootball.com/cw/icons/apple-v2-dark.svg"
          width={32}
          height={32}
        />
      </AppleLogin>
      <Button
        role="button"
        className={styles.socialButton}
        onClick={onEmailClick}
      >
        <ImageWithFallback
          alt={t("ICON_EMAIL_ALT_TEXT")}
          src="https://images.onefootball.com/cw/icons/email-light.svg"
          darkModeSrc="https://images.onefootball.com/cw/icons/email-dark.svg"
          width={32}
          height={32}
        />
      </Button>
    </div>
  );
};

export { SocialLogin };
